import { clientNeoFact } from "@neonumy/api-clients";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";
import {
  LIST_TOTALS,
  PRIORITY_TASKS,
  LIST_USER_SELLS,
  LIST_TOP_CLIENTS,
  LIST_DIARY_STATS,
  LIST_TOP_SELL_PRODUCTS,
  LIST_TOP_SELL_CATEGORIES,
} from "queries/neoFact/dashboard";
import { LIST_SALE_DOCUMENTS_LATEST_MODIFIED } from "queries/neoFact/documents";
import useDashboardStore from "views/hooks/useDashboardStore";

export default function useDashboardApi() {
  const { t } = useTranslation();

  const setBudgetNotInvoicedValue = useDashboardStore(state => state.setBudgetNotInvoicedValue);
  const setPaymentWaitingValue = useDashboardStore(state => state.setPaymentWaitingValue);
  const setPaymentsValue = useDashboardStore(state => state.setPaymentsValue);
  const setInvoicedValue = useDashboardStore(state => state.setInvoicedValue);

  const [
    listTotals,
    {
      refetch: refetchListTotals,
      loading: loadingListTotals,
      data: listTotalsData,
      error: listTotalsError,
    },
  ] = useLazyQuery<TotalsStatsDataTypes, ListTotalsVars>(LIST_TOTALS, {
    client: clientNeoFact,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (listTotalsError) onErrorGraphql(listTotalsError);
  }, [listTotalsError]);

  const totalStatsData: TotalsStatsDataTypes | undefined = listTotalsData;

  const [
    listTopClients,
    {
      refetch: refetchListTopClients,
      loading: loadingListTopClients,
      data: listTopClientsData,
      error: listTopClientsError,
    },
  ] = useLazyQuery<ListTopClientsResponse, ListTopClientsVars>(LIST_TOP_CLIENTS, {
    client: clientNeoFact,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (listTopClientsError) onErrorGraphql(listTopClientsError);
  }, [listTopClientsError]);

  const topClientsData = useMemo(() => {
    const listTopClients = listTopClientsData?.listTopClients;
    if (!listTopClients) return undefined;

    return {
      labels: listTopClients.map(item => item?.client?.name),
      datasets: [
        {
          data: listTopClients.map(item => Math.abs(Number(item?.totalValueDefaultCurrency) || 0)),
          backgroundColor: [
            "#3e849e",
            "#ffe119",
            "#e6194b",
            "#3cb44b",
            "#ff7f00",
            "#4363d8",
            "#f58231",
            "#29bdab",
            "#911eb4",
            "#46f0f0",
            "#f032e6",
            "#3998f5",
            "#bcf60c",
            "#fabebe",
            "#008080",
            "#e6beff",
            "#235b54",
            "#e68f66",
            "#9a6324",
            "#fffac8",
            "#800000",
            "#aaffc3",
            "#fcff5d",
            "#772b9d",
            "#808000",
            "#ffd8b1",
            "#f22020",
            "#000075",
            "#808080",
            "#000000",
          ],
          spacing: 3,
          borderWidth: 0,
          borderRadius: 8,
        },
      ],
    };
  }, [listTopClientsData]);

  const [
    listUserSells,
    {
      refetch: refetchListUserSellsClients,
      loading: loadingUserSellsClients,
      data: listUserSellsData,
      error: listUserSellsError,
    },
  ] = useLazyQuery<ListUserSellsResponse, ListUserSellsVars>(LIST_USER_SELLS, {
    client: clientNeoFact,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (listUserSellsError) onErrorGraphql(listUserSellsError);
  }, [listUserSellsError]);

  const toplistUsersData = useMemo(() => {
    const listUserSells = listUserSellsData?.listUserSells;
    if (!listUserSells) return undefined;

    return {
      labels: listUserSells.map(
        item => `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`,
      ),
      datasets: [
        {
          label: t("Invoice amount"),
          data: listUserSells.map(item => Math.abs(Number(item?.totalBilledDefaultCurrency) || 0)),
          backgroundColor: ["#3e849e"],
          spacing: 1,
        },
        {
          label: t("Estimated amount"),
          data: listUserSells.map(item =>
            Math.abs(Number(item?.totalBudgetedDefaultCurrency) || 0),
          ),
          backgroundColor: ["rgb(235, 197, 33)"],
          spacing: 1,
        },
      ],
    };
  }, [listUserSellsData, t]);

  const [
    listTopSellProducts,
    {
      refetch: refetchListTopSellProducts,
      loading: loadingListTopSellProducts,
      data: listTopSellProductsQueryData,
      error: listTopSellProductsError,
    },
  ] = useLazyQuery<ListTopSellProductsResponse, ListTopSellProductsVars>(LIST_TOP_SELL_PRODUCTS, {
    client: clientNeoFact,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (listTopSellProductsError) onErrorGraphql(listTopSellProductsError);
  }, [listTopSellProductsError]);

  const listTopSellProductsData: ListTopSellProductsResults | undefined =
    listTopSellProductsQueryData?.listTopSellProducts?.map(item => ({
      ...item,
      key: item.reference,
    }));

  const [
    listTopSellCategories,
    {
      refetch: refetchListTopSellCategoriesProducts,
      loading: loadingListTopSellCategoriesProducts,
      data: listTopSellCategoriesQueryData,
      error: listTopSellCategoriesError,
    },
  ] = useLazyQuery<ListTopSellCategoriesResponse, ListTopSellCategoriesVars>(
    LIST_TOP_SELL_CATEGORIES,
    {
      client: clientNeoFact,
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (listTopSellCategoriesError) onErrorGraphql(listTopSellCategoriesError);
  }, [listTopSellCategoriesError]);

  const listTopSellCategoriesData: ListTopSellCategoriesResults | undefined =
    listTopSellCategoriesQueryData?.listTopSellCategories?.map(item => ({
      ...item,
      key: item.reference,
    }));

  const [
    listDiaryStats,
    {
      refetch: refetchListDiaryStats,
      loading: loadingListDiaryStats,
      data: listDiaryStatsQueryData,
      error: listDiaryStatsError,
    },
  ] = useLazyQuery<ListDiaryStatsResponse, ListDiaryStatsVars>(LIST_DIARY_STATS, {
    client: clientNeoFact,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (listDiaryStatsError) onErrorGraphql(listDiaryStatsError);
  }, [listDiaryStatsError]);

  const listDiaryStatsData = useMemo(() => {
    const listDiaryStats = listDiaryStatsQueryData?.listDiaryStats;
    if (!listDiaryStats) return undefined;

    const labels = new Set(
      listDiaryStats.payments
        .map(item => item.date)
        .concat(listDiaryStats.saleDocument.map(item => item.date))
        .concat(listDiaryStats.budgetNotSaleDocument.map(item => item.date))
        .concat(listDiaryStats.paymentWaiting.map(item => item.date)),
    );

    return {
      labels: Array.from(labels),
      datasets: [
        {
          label: t("Revenues"),
          data: listDiaryStats?.payments?.map(item => Math.abs(Number(item?.value))),
          backgroundColor: ["rgb(186, 198, 44)"],
          borderColor: "rgb(191, 198, 103)",
          spacing: 1,
        },
        {
          label: t("Receipts"),
          data: listDiaryStats?.saleDocument.map(item => Math.abs(Number(item?.value))),
          backgroundColor: ["rgb(62, 132, 158)"],
          borderColor: "rgb(159, 185, 194)",
          spacing: 1,
        },
        {
          label: t("Quote pending"),
          data: listDiaryStats?.budgetNotSaleDocument.map(item => Math.abs(Number(item?.value))),
          backgroundColor: ["rgb(110, 193, 180)"],
          borderColor: "rgb(149, 193, 186)",
          spacing: 1,
        },
        {
          label: t("In progress"),
          data: listDiaryStats?.paymentWaiting.map(item => Math.abs(Number(item?.value))),
          backgroundColor: ["rgb(235, 197, 33)"],
          borderColor: "rgb(235, 215, 127)",
          spacing: 1,
        },
      ],
    };
  }, [listDiaryStatsQueryData, t]);

  useEffect(() => {
    const listDiaryStats = listDiaryStatsQueryData?.listDiaryStats;
    if (!listDiaryStats) return;

    const sumValues = (items: ListDiaryDataValueTypes[]) =>
      items.reduce((acc, curr) => acc + Number(curr.value), 0);

    setInvoicedValue(sumValues(listDiaryStats.saleDocument));
    setPaymentsValue(sumValues(listDiaryStats.payments));
    setPaymentWaitingValue(sumValues(listDiaryStats.paymentWaiting));
    setBudgetNotInvoicedValue(sumValues(listDiaryStats.budgetNotSaleDocument));
  }, [listDiaryStatsQueryData]);

  const [
    getPriorityTasks,
    {
      refetch: refetchGetPriorityTasks,
      loading: priorityTasksLoading,
      data: getPriorityTasksData,
      error: getPriorityTasksError,
    },
  ] = useLazyQuery<GetPriorityTasksResponse, GetVarTypeCompanyId>(PRIORITY_TASKS, {
    client: clientNeoFact,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (getPriorityTasksError) onErrorGraphql(getPriorityTasksError);
  }, [getPriorityTasksError]);

  const priorityTasks: GetPriorityTasksResponseData | undefined =
    getPriorityTasksData?.getPriorityTasks;

  const [
    listSaleDocumentsLatestModified,
    {
      loading: listSaleDocumentsLatestModifiedloading,
      data: listSaleDocumentsLatestModifiedData,
      error: listSaleDocumentsLatestModifiedError,
    },
  ] = useLazyQuery<ListSaleDocsModifiedResponse, ListInvoicesVars>(
    LIST_SALE_DOCUMENTS_LATEST_MODIFIED,
    {
      client: clientNeoFact,
      fetchPolicy: "cache-and-network",
    },
  );

  useEffect(() => {
    if (listSaleDocumentsLatestModifiedError) onErrorGraphql(listSaleDocumentsLatestModifiedError);
  }, [listSaleDocumentsLatestModifiedError]);

  const lastDocsModified: ListSaleDocsModifiedData[] | undefined =
    listSaleDocumentsLatestModifiedData?.listSaleDocuments?.results?.map(item => ({
      ...item,
      key: item.id,
    }));

  return {
    listTotals,
    listUserSells,
    priorityTasks,
    listTopClients,
    listDiaryStats,
    topClientsData,
    totalStatsData,
    toplistUsersData,
    getPriorityTasks,
    lastDocsModified,
    loadingListTotals,
    refetchListTotals,
    listDiaryStatsData,
    listTopSellProducts,
    priorityTasksLoading,
    loadingListDiaryStats,
    loadingListTopClients,
    refetchListDiaryStats,
    refetchListTopClients,
    listTopSellCategories,
    refetchGetPriorityTasks,
    listTopSellProductsData,
    loadingUserSellsClients,
    listTopSellCategoriesData,
    refetchListTopSellProducts,
    loadingListTopSellProducts,
    refetchListUserSellsClients,
    listSaleDocumentsLatestModified,
    loadingListTopSellCategoriesProducts,
    refetchListTopSellCategoriesProducts,
    listSaleDocumentsLatestModifiedloading,
  };
}
